import { create } from 'zustand';

import { ChannelPolicy, UserInit } from '@/api/login/types.ts';
import ProfileController from '@/api/profile/profile.ts';
import {
  SearchFiltersPayload,
  UpdateProfilePayload
} from '@/api/profile/types.ts';
import { helpers } from '@/helpers.ts';
import {
  BodyType,
  EmploymentSector,
  PhotoState,
  UserState,
  UserStateReason,
  VideoState,
  ZodiacSign
} from '@/enums/apiEnums.ts';
import { ViewPhotoState } from '@/enums/viewEnums.ts';
import { VideoKind } from '@/shared/type/userDataFields.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import {
  readPendingVideo,
  removePendingVideo
} from '@/utils/pendingVideoCache.ts';

interface Premium {
  expireTime: string | null;
  isExpired: boolean;
  duration: number;
}

interface ProfileStore {
  aboutMe: UserInit | null;
  premium: Premium;
  remainingAmount: number;
  isFetching: boolean;
  isSkipCompleteProfilePlaceholder: boolean;
  setIsSkipCompleteProfilePlaceholder: (skipped: boolean) => void;
  setAboutMe: (data: UserInit | null) => void;
  setRemainingAmount: (data: UserInit | null) => void;
  updatePolicy: (payload?: ChannelPolicy) => Promise<unknown>;
  updateProfile: (
    payload?: UpdateProfilePayload,
    options?: { withDelay?: number }
  ) => Promise<unknown>;
  verifyUser: (isVerified?: boolean) => void;
  hideProfile: () => Promise<void>;
  showProfile: () => Promise<void>;
  shadowProfile: () => Promise<void>;
  unshadowProfile: () => Promise<void>;
  updateRemainingAmount: (quantity: number) => void;
  updatePhoto: (hid: string, action: ViewPhotoState) => void;
  updateVideo: (hid: string, action: ViewPhotoState) => void;
  updatePremium: (params: Omit<Premium, 'isExpired'>) => void;
  updateSearchFilters: (payload: SearchFiltersPayload) => Promise<unknown>;
  getProfileCompletionPercentage: () => number;
  isProfileFullyCompleted: () => boolean;
}

export const useProfileStore = create<ProfileStore>((set, get) => ({
  aboutMe: null,
  premium: {
    expireTime: null,
    isExpired: true,
    duration: 0
  },
  isFetching: false,
  isSkipCompleteProfilePlaceholder: false,
  setIsSkipCompleteProfilePlaceholder: (skipped: boolean) => {
    set({ isSkipCompleteProfilePlaceholder: skipped });
  },
  remainingAmount: 0,
  setAboutMe: (data) => {
    set({ aboutMe: data });

    // Restore video from IndexedDB if server hasn't processed it yet
    const serverVideo = data?.profile?.v2videos?.[0];
    const hasVariants =
      serverVideo?.variants && serverVideo.variants.length > 0;

    if (serverVideo && !hasVariants) {
      void readPendingVideo().then((cached) => {
        if (cached && cached.hid === serverVideo.hid) {
          set((s) => ({
            aboutMe: s.aboutMe
              ? {
                  ...s.aboutMe,
                  profile: {
                    ...s.aboutMe.profile,
                    v2videos: [
                      {
                        ...serverVideo,
                        variants: [
                          { url: cached.url, kind: VideoKind.KIND_VIDEO },
                          { url: cached.url, kind: VideoKind.KIND_POSTER }
                        ]
                      }
                    ]
                  }
                }
              : s.aboutMe
          }));
        }
      });
    } else {
      // Server has variants or no video at all — clean up cache
      void removePendingVideo();
    }
  },
  setRemainingAmount: (data) => {
    set({
      remainingAmount:
        data?.one_time_purchases?.find((p) => p.product.name === 'gift.generic')
          ?.remaining_amount ?? 0
    });
  },
  updatePolicy: async (payload) => {
    try {
      if (payload) {
        await ProfileController.updatePolicy({ channel_policy: payload });
      }

      set((state) => ({
        aboutMe: state.aboutMe
          ? {
              ...state.aboutMe,
              channel_policy: payload
            }
          : null
      }));
    } catch (e) {
      catchErrors(e, true);
    }
  },
  updateProfile: async (payload, options) => {
    set({ isFetching: true });

    try {
      if (payload) {
        await ProfileController.updateProfile(payload);
      }

      const profile = options?.withDelay
        ? await helpers.executeWithDelay(
            ProfileController.getProfile,
            options?.withDelay
          )
        : await ProfileController.getProfile();

      set((state) => {
        // Preserve local blob video that server doesn't know about yet
        const localVideo = state.aboutMe?.profile?.v2videos?.[0];
        const hasLocalBlob =
          localVideo &&
          localVideo.variants?.some((v) => v.url.startsWith('blob:'));

        const mergedVideos = hasLocalBlob ? [localVideo] : profile.v2videos;

        return {
          aboutMe: {
            ...state.aboutMe,
            profile: { ...profile, v2videos: mergedVideos }
          }
        };
      });

      // After setting state, check if we need to restore video from IndexedDB
      const serverVideo = profile?.v2videos?.[0];
      const hasVariants =
        serverVideo?.variants && serverVideo.variants.length > 0;

      if (serverVideo && !hasVariants) {
        void readPendingVideo().then((cached) => {
          if (!cached || cached.hid !== serverVideo.hid) return;
          const currentState = get();
          const currentVideo = currentState.aboutMe?.profile?.v2videos?.[0];

          if (
            !currentVideo?.variants?.length ||
            currentVideo.variants.some((v) => v.url.startsWith('blob:'))
          ) {
            set((s) => ({
              aboutMe: s.aboutMe
                ? {
                    ...s.aboutMe,
                    profile: {
                      ...s.aboutMe.profile,
                      v2videos: [
                        {
                          ...serverVideo,
                          variants: [
                            { url: cached.url, kind: VideoKind.KIND_VIDEO },
                            { url: cached.url, kind: VideoKind.KIND_POSTER }
                          ]
                        }
                      ]
                    }
                  }
                : s.aboutMe
            }));
          }
        });
      } else {
        void removePendingVideo();
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      set({ isFetching: false });
    }
  },
  hideProfile: async () => {
    try {
      await ProfileController.hideProfile();

      set((state) =>
        state.aboutMe?.profile
          ? {
              aboutMe: {
                ...state.aboutMe,
                profile: {
                  ...state.aboutMe.profile,
                  state: UserState.STATE_HIDDEN
                }
              }
            }
          : state
      );
    } catch (e) {
      catchErrors(e, true);
    }
  },
  showProfile: async () => {
    try {
      await ProfileController.showProfile();

      set((state) =>
        state.aboutMe?.profile
          ? {
              aboutMe: {
                ...state.aboutMe,
                profile: {
                  ...state.aboutMe.profile,
                  state: UserState.STATE_ACTIVE
                }
              }
            }
          : state
      );
    } catch (e) {
      catchErrors(e, true);
    }
  },
  shadowProfile: async () => {
    try {
      await ProfileController.shadowProfile();

      set((state) =>
        state.aboutMe?.profile
          ? {
              aboutMe: {
                ...state.aboutMe,
                profile: {
                  ...state.aboutMe.profile,
                  state: UserState.STATE_SHADOWED,
                  state_reason: UserStateReason.STATE_REASON_VOLUNTARY
                }
              }
            }
          : state
      );
    } catch (e) {
      catchErrors(e, true);
    }
  },
  unshadowProfile: async () => {
    try {
      await ProfileController.unshadowProfile();

      set((state) =>
        state.aboutMe?.profile
          ? {
              aboutMe: {
                ...state.aboutMe,
                profile: {
                  ...state.aboutMe.profile,
                  state: UserState.STATE_ACTIVE,
                  state_reason: UserStateReason.STATE_REASON_UNSPECIFIED
                }
              }
            }
          : state
      );
    } catch (e) {
      catchErrors(e, true);
    }
  },
  verifyUser: (isVerified = false) => {
    set((state) => {
      if (!state.aboutMe) return state;

      return {
        aboutMe: {
          ...state.aboutMe,
          profile: {
            ...state.aboutMe.profile,
            state: isVerified
              ? UserState.STATE_ACTIVE
              : UserState.STATE_PENDING,
            state_reason: isVerified
              ? UserStateReason.STATE_REASON_UNSPECIFIED
              : UserStateReason.STATE_REASON_VERIFICATION_FAILED
          }
        }
      };
    });
  },
  updateRemainingAmount: (remaining_amount: number) => {
    const { remainingAmount } = get();

    set({ remainingAmount: remainingAmount + remaining_amount });
  },
  updatePhoto: async (hid, action) => {
    const { aboutMe, setAboutMe, updateProfile } = get();

    if (!aboutMe || !aboutMe.profile || !aboutMe.profile.v2photos) {
      return;
    }

    const updatedPhotos =
      action === ViewPhotoState.APPROVE
        ? aboutMe.profile.v2photos.map((photo) =>
            photo.hid === hid
              ? { ...photo, state: PhotoState.STATE_APPROVED }
              : photo
          )
        : aboutMe.profile.v2photos.filter((photo) => photo.hid !== hid);

    setAboutMe({
      ...aboutMe,
      profile: {
        ...aboutMe.profile,
        v2photos: updatedPhotos
      }
    });

    try {
      await updateProfile();
    } catch (e) {
      catchErrors(e, true);
    }
  },
  updateVideo: async (hid, action) => {
    const { aboutMe, setAboutMe, updateProfile } = get();

    if (!aboutMe || !aboutMe.profile || !aboutMe.profile.v2videos) {
      return;
    }

    const updatedVideos =
      action === ViewPhotoState.APPROVE
        ? aboutMe.profile.v2videos.map((video) =>
            video.hid === hid
              ? { ...video, state: VideoState.STATE_ACTIVE, in_review: false }
              : video
          )
        : aboutMe.profile.v2videos.filter((video) => video.hid !== hid);

    setAboutMe({
      ...aboutMe,
      profile: {
        ...aboutMe.profile,
        v2videos: updatedVideos
      }
    });

    try {
      await updateProfile();
    } catch (e) {
      catchErrors(e, true);
    }
  },
  updatePremium: (params) => {
    set({
      premium: {
        expireTime: params.expireTime,
        isExpired: params.expireTime
          ? Date.now() > new Date(params.expireTime).getTime()
          : true,
        duration: params.duration
      }
    });
  },
  updateSearchFilters: async (payload: SearchFiltersPayload) => {
    try {
      await ProfileController.updateSearchFilters(payload);

      set((state) => {
        if (!state.aboutMe?.profile) return state;

        return {
          aboutMe: {
            ...state.aboutMe,
            profile: {
              ...state.aboutMe.profile,
              ...(payload.age_filter_min && {
                age_filter_min: payload.age_filter_min
              }),
              ...(payload.age_filter_max && {
                age_filter_max: payload.age_filter_max
              }),
              ...(payload.search_gender && {
                search_gender: payload.search_gender
              })
            }
          }
        };
      });
    } catch (e) {
      catchErrors(e, true);
    }
  },
  getProfileCompletionPercentage: () => {
    const { aboutMe } = get();

    if (!aboutMe?.profile) {
      return 0;
    }

    let completionPercentage = 30;

    if (aboutMe.profile.v2photos.length === 3) {
      completionPercentage += 10;
    }

    if (aboutMe.profile.about_me && aboutMe.profile.about_me.trim() !== '') {
      completionPercentage += 10;
    }

    if (aboutMe.profile.height_cm > 0) {
      completionPercentage += 10;
    }

    if (
      aboutMe.profile.employment_sector !==
      EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED
    ) {
      completionPercentage += 10;
    }

    if (aboutMe.profile.body_type !== BodyType.BODY_TYPE_UNSPECIFIED) {
      completionPercentage += 10;
    }

    if (aboutMe.profile.zodiac_sign !== ZodiacSign.ZODIAC_SIGN_UNSPECIFIED) {
      completionPercentage += 10;
    }

    if (aboutMe.profile.spoken_languages.length !== 0) {
      completionPercentage += 10;
    }

    return completionPercentage;
  },
  isProfileFullyCompleted: () => {
    return get().getProfileCompletionPercentage() === 100;
  }
}));
